import React from 'react';
import { ICONS } from 'src/constants/icons';
import { LOCALES, WINDOW_SIZES } from 'src/constants/main';
import { staticTheme } from 'src/constants/staticTheme';
import { localeStore } from 'src/mobx/localesStore';
import { IProductInsideCombo } from 'src/types/combo';
import { PRODUCT_TYPE } from 'src/types/products';
import { formatPrice, formatUnit, getTranslate } from 'src/utils/formatter';
import { getComboItemUnavailableMessage } from 'src/utils/helpers';
import { getProductCalories, getProductUnits } from 'src/utils/product';
import Icon from 'src/view/components/Controls/Icon/Icon';
import PopupModal from 'src/view/components/Controls/Popup/Popup';
import Image from 'src/view/components/Image/Image';
import ResponsiveContent from 'src/view/components/ResponsiveContent/ResponsiveContent';
import DealComboItemModal from '../DealComboItemModal/DealComboItemModal';
import './style.scss';

interface IProps {
  item: IProductInsideCombo;
  index?: number;
  selected?: boolean;
  className?: string;
  type?: PRODUCT_TYPE;
  selectItem: (item: IProductInsideCombo) => void;
  clickOnItem?: (item: IProductInsideCombo) => void;
}

interface IState {
  modal: boolean;
}

export default class ComboItem extends React.Component<IProps, IState> {
  /**
   * return list of units [calories, additional unit]
   *
   * @readonly
   * @memberof MenuProduct
   */
  get units() {
    return getProductUnits(this.props.item);
  }

  state = {
    modal: false,
  };

  selectItem = () => {
    this.props.selectItem(this.props.item);
  };

  handleItemClick = () => {
    if (this.props.clickOnItem) {
      this.props.clickOnItem(this.props.item);
      return;
    }

    this.setState({ modal: true });
  };

  renderModalContent = ({ closeModal }: { closeModal: () => void }) => {
    const close = () => {
      closeModal();
    };
    return (
      <DealComboItemModal
        item={this.props.item}
        clickClose={close}
        isMeal={this.props.type === PRODUCT_TYPE.MEAL_COMBO}
      />
    );
  };

  renderUnitBlock = (): JSX.Element => {
    const { item } = this.props;
    const isMealCombo: boolean = this.props.type === PRODUCT_TYPE.MEAL_COMBO;

    return (
      <div className={`combo-item__unit justify-content-${isMealCombo ? 'between' : 'end'}`}>
        {isMealCombo ? (
          <p className="family-semibold color-primary text-size-12 text-uppercase">{formatPrice(Number(item.price))}</p>
        ) : null}
        <p className="family-regular color-gray text-size-10 text-end">
          {[getProductCalories(item as any)].map((unit) => (unit ? <span className="ml-1">{unit}</span> : null))}
        </p>
      </div>
    );
  };

  renderUnavailableMessage = (): JSX.Element | null => {
    if (!this.unavailableMessage) return null;

    return (
      <p className="combo-item__unavailable-message family-bold color-red text-size-10">
        {localeStore.t(this.unavailableMessage)}
      </p>
    );
  };

  get itemDescription(): string | undefined {
    if (!this.props.item.description) return undefined;
    return [...this.units, getTranslate(this.props.item.description)].filter(Boolean).join(' • ');
  }

  get isArabic(): boolean {
    return localeStore.language === LOCALES.AR;
  }
  get unavailableMessage(): string | undefined {
    return getComboItemUnavailableMessage(this.props.item);
  }

  get itemClassName(): string {
    let className: string = 'combo-item';

    if (this.props.index) className += ' pxml-4';
    if (this.props.className) className += ` ${this.props.className}`;
    if (this.props.item.is_selected || this.props.selected) className += ' border_pseudo-primary';
    // if (this.unavailableMessage) className += ' combo-item__unavailable';

    return className;
  }

  render() {
    return (
      <>
        <ResponsiveContent min={WINDOW_SIZES.md}>
          <li className={this.itemClassName} onClick={this.selectItem}>
            <div className="combo-item__container-image">
              <Image
                aspectRatio={1.263}
                src={this.props.item.image}
                wrapperClassName={this.unavailableMessage ? 'combo-item__unavailable' : undefined}
              />
              {this.renderUnavailableMessage()}
            </div>
            <div className="combo-item__text-wrapper d-flex flex-column full-height justify-content-between">
              <p className="combo-item__text family-bold color-dark">{getTranslate(this.props.item.name)}</p>
              {this.renderUnitBlock()}
            </div>
          </li>
        </ResponsiveContent>

        <ResponsiveContent max={WINDOW_SIZES.md}>
          <PopupModal
            justify="center"
            position="top"
            align="center"
            selfClose
            withCloseIcon
            content={this.renderModalContent}
            onClose={() => this.setState({ modal: false })}
            mobile={{ popup: true, nonPane: true }}
            visible={this.state.modal}
            modalClassName="deal-combo-item-modal"
            backdropClassName="combo-item__modal-backdrop"
          >
            <li className={this.itemClassName} onClick={this.handleItemClick}>
              <div className="combo-item__container-image">
                <Image
                  aspectRatio={85 / 76}
                  src={this.props.item.image}
                  wrapperClassName={this.unavailableMessage ? 'combo-item__unavailable' : undefined}
                />
                {this.renderUnavailableMessage()}
              </div>
              <div className="combo-item__text-wrapper full-height d-flex flex-column full-height justify-content-center">
                <p className="combo-item__text family-semibold color-dark text-size-18 text-overflow">
                  {getTranslate(this.props.item.name)}
                </p>
                {this.itemDescription ? (
                  <p className="combo-item__text_description combo-item__text family-regular color-gray text-size-12">
                    {this.itemDescription}
                  </p>
                ) : null}
              </div>
              <Icon
                name={this.isArabic ? ICONS.SLIDER_LEFT : ICONS.SLIDER_RIGHT}
                color={staticTheme.colors.color.gray}
                size={16}
              />
            </li>
          </PopupModal>
        </ResponsiveContent>
      </>
    );
  }
}
